import React, { useEffect, useRef, useState } from 'react';
import { X, Download, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import FacturarButton from './FacturarButton';

export default function LiquidacionImagenModal({ liquidacionId, facturasElectronicas, onClose }) {
  const [datos, setDatos] = useState(null);
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [fes, setFes] = useState(Array.isArray(facturasElectronicas) ? facturasElectronicas : []);

  useEffect(() => {
    api.get(`/liquidaciones/${liquidacionId}/datos_imagen/`)
      .then((res) => setDatos(res.data))
      .catch(() => toast.error('No se pudieron cargar los datos de la liquidación.'));
  }, [liquidacionId]);

  useEffect(() => {
    if (!datos) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = 2;
    const W = 480;
    // Dibuja la liquidación en un lienzo de alto H y devuelve el alto que realmente necesita.
    // Cambiar el tamaño de un canvas lo borra, así que si hay más filas que las que caben
    // (conceptos adicionales) se vuelve a dibujar completo con el alto correcto.
    const render = (H) => {
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);

      // Colors
      const YELLOW = '#FFC107';
      const DARK = '#1a1a2e';
      const WHITE = '#ffffff';
      const GRAY = '#f8f9fa';
      const GRAY_TEXT = '#6b7280';
      const PRIMARY = '#0ea5e9';
      const BORDER = '#e5e7eb';

      // Background
      ctx.fillStyle = WHITE;
      ctx.fillRect(0, 0, W, H);

      // Header bar
      ctx.fillStyle = YELLOW;
      ctx.fillRect(0, 0, W, 80);

      // Logo area - draw a simple package icon
      ctx.fillStyle = DARK;
      ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ECUAFLY', W / 2, 38);
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#333';
      ctx.fillText('Tu courier de confianza', W / 2, 58);

      // Title
      ctx.fillStyle = DARK;
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('LIQUIDACIÓN DE PAQUETE', W / 2, 110);

      // Separator line
      ctx.strokeStyle = PRIMARY;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(40, 120);
      ctx.lineTo(W - 40, 120);
      ctx.stroke();

      // Client info section
      let y = 145;
      const leftX = 40;
      const rightX = W - 40;

      const drawRow = (label, value, opts = {}) => {
        if (opts.section) {
          ctx.fillStyle = GRAY;
          ctx.fillRect(leftX, y - 4, rightX - leftX, 28);
        }
        if (opts.bold) {
          ctx.fillStyle = DARK;
          ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
        } else {
          ctx.fillStyle = '#374151';
          ctx.font = '13px system-ui, -apple-system, sans-serif';
        }
        ctx.textAlign = 'left';
        ctx.fillText(label, leftX + 8, y + 13);
        ctx.textAlign = 'right';
        if (opts.highlight) {
          ctx.fillStyle = PRIMARY;
          ctx.font = 'bold 15px system-ui, -apple-system, sans-serif';
        } else if (opts.bold) {
          ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
        }
        ctx.fillText(value, rightX - 8, y + 13);

        if (!opts.noBorder) {
          ctx.strokeStyle = BORDER;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(leftX, y + 24);
          ctx.lineTo(rightX, y + 24);
          ctx.stroke();
        }
        y += 28;
      };

      // Client & Guide
      ctx.fillStyle = DARK;
      ctx.font = '12px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Cliente:', leftX + 8, y + 10);
      ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(datos.cliente_nombre, rightX - 8, y + 10);
      y += 26;

      ctx.font = '12px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Guía:', leftX + 8, y + 10);
      ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(datos.guia || '—', rightX - 8, y + 10);
      y += 22;

      if (datos.trackings && datos.trackings.length > 0) {
        ctx.font = '9px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = GRAY_TEXT;
        ctx.textAlign = 'right';
        const trackingText = datos.trackings.join('  ·  ');
        const maxW = rightX - leftX - 16;
        if (ctx.measureText(trackingText).width <= maxW) {
          ctx.fillText(trackingText, rightX - 8, y + 8);
          y += 16;
        } else {
          for (const t of datos.trackings) {
            ctx.fillText(t, rightX - 8, y + 8);
            y += 13;
          }
          y += 3;
        }
      } else {
        y += 8;
      }

      // Separator
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(leftX, y);
      ctx.lineTo(rightX, y);
      ctx.stroke();
      y += 10;

      // Detail rows
      drawRow('Peso en libras', datos.peso_libras.toFixed(2));
      drawRow('Precio de envío p/libra', '$' + datos.tarifa_por_libra.toFixed(2));
      drawRow('Total Envío', '$' + datos.total_envio.toFixed(2), { bold: true });

      if (datos.servicio_aduanero > 0) {
        drawRow('Servicio aduanero', '$' + datos.servicio_aduanero.toFixed(2));
      }
      drawRow('Consolidación', '$' + datos.consolidacion.toFixed(2));
      if (datos.entrega_domicilio > 0) {
        drawRow('Entrega a domicilio', '$' + datos.entrega_domicilio.toFixed(2));
      }
      (datos.otros_conceptos || []).forEach((o) => drawRow(o.concepto, '$' + Number(o.monto).toFixed(2)));

      // Subtotal section
      drawRow('Subtotal', '$' + datos.subtotal.toFixed(2), { bold: true, section: true });
      drawRow('IVA', '$' + datos.iva.toFixed(2));
      drawRow('Total Courier', '$' + datos.total_courier.toFixed(2), { bold: true, section: true });

      // Reembolsos
      const reemb = datos.reembolsos || {};
      if (reemb['Aduana'] !== undefined) drawRow('Aduana', '$' + Number(reemb['Aduana']).toFixed(2));
      if (reemb['Fodinfa'] !== undefined) drawRow('Fodinfa', '$' + Number(reemb['Fodinfa']).toFixed(2));
      if (reemb['Gastos Bancarios'] !== undefined) drawRow('Gastos Bancarios', '$' + Number(reemb['Gastos Bancarios']).toFixed(2));

      // Total a Pagar
      y += 6;
      ctx.fillStyle = DARK;
      ctx.fillRect(leftX, y, rightX - leftX, 36);
      ctx.fillStyle = WHITE;
      ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Total a Pagar', leftX + 12, y + 23);
      ctx.fillStyle = YELLOW;
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('$' + datos.total_a_pagar.toFixed(2), rightX - 12, y + 24);
      y += 46;

      // Date
      ctx.fillStyle = GRAY_TEXT;
      ctx.font = '10px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Fecha: ' + datos.fecha, W / 2, y + 10);
      y += 20;

      // Footer
      const footerY = Math.max(y + 10, H - 60);
      ctx.fillStyle = DARK;
      ctx.fillRect(0, footerY, W, H - footerY);
      ctx.fillStyle = YELLOW;
      ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ECUAFLY', W / 2, footerY + 20);
      ctx.fillStyle = '#9ca3af';
      ctx.font = '9px system-ui, -apple-system, sans-serif';
      ctx.fillText('Miami · Shenzhen · Quito  |  info@ecuafly.com', W / 2, footerY + 36);
      return footerY + 50;
    };

    const H0 = 720;
    const finalH = render(H0);
    if (finalH > H0) render(finalH);

    setImgUrl(canvas.toDataURL('image/png'));
  }, [datos]);

  const descargar = () => {
    if (!imgUrl) return;
    const link = document.createElement('a');
    link.download = `liquidacion_${datos?.guia || liquidacionId}.png`;
    link.href = imgUrl;
    link.click();
    toast.success('Imagen descargada.');
  };

  const copiar = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('Imagen copiada al portapapeles.');
    } catch {
      toast.error('No se pudo copiar. Descarga la imagen en su lugar.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Liquidación para WhatsApp</h3>
          <button className="btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
          {!datos ? (
            <p className="text-secondary">Cargando...</p>
          ) : (
            <>
              <canvas ref={canvasRef} style={{ maxWidth: '100%', borderRadius: 'var(--radius-md)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'center', marginTop: 'var(--spacing-4)' }}>
                <button className="btn-primary" onClick={descargar}>
                  <Download size={16} /> Descargar imagen
                </button>
                <button className="btn-secondary" onClick={copiar}>
                  <Copy size={16} /> Copiar al portapapeles
                </button>
              </div>
              <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                <FacturarButton
                  liquidacionId={liquidacionId}
                  facturasElectronicas={fes}
                  onFacturada={(data) => {
                    // Combina por tipo (servicios/reembolsos) en vez de
                    // reemplazar todo: si solo se emitió el tipo que
                    // faltaba, no hay que "olvidar" el otro que ya estaba
                    // vigente de antes.
                    const nuevas = Array.isArray(data) ? data : [data];
                    setFes((prev) => {
                      const porTipo = Object.fromEntries(prev.map((f) => [f.tipo, f]));
                      nuevas.forEach((f) => { porTipo[f.tipo] = f; });
                      return Object.values(porTipo);
                    });
                  }}
                />
                {fes.length > 0 && fes.every((f) => f.estado === 'autorizada') && (
                  <span className="badge" style={{ background: 'var(--color-success)', color: '#fff', fontSize: '11px' }}>
                    {fes.length} Autorizada{fes.length > 1 ? 's' : ''}
                  </span>
                )}
                {fes.some((f) => f.estado === 'rechazada') && (
                  <span className="badge" style={{ background: 'var(--color-error)', color: '#fff', fontSize: '11px' }}>
                    Rechazada
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
